import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'

/**
 * Modal 组件的 props 接口
 */
interface ModalProps {
  /** 是否显示模态框 */
  isOpen: boolean
  /** 关闭模态框的回调函数 */
  onClose: () => void
  /** 模态框标题 */
  title?: string
  /** 模态框头部额外操作区域 */
  headerExtra?: React.ReactNode
  /** 模态框内容 */
  children: React.ReactNode
  /** 模态框底部内容（固定显示） */
  footer?: React.ReactNode
  /** 模态框大小 */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  /** 是否显示关闭按钮 */
  showCloseButton?: boolean
  /** 点击背景是否关闭 */
  closeOnBackdropClick?: boolean
  /** 按 ESC 键是否关闭 */
  closeOnEscape?: boolean
  /** 自定义类名 */
  className?: string
  /** 模态框内容区域类名 */
  contentClassName?: string
  /** 模态框主体区域类名 */
  bodyClassName?: string
  /** 是否禁止滚动 */
  preventScroll?: boolean
  /** z-index 层级，用于嵌套 Modal 时设置更高层级 */
  zIndex?: number
  /** 其他属性 */
  [key: string]: any
}

/**
 * 模态框组件
 * 支持多种尺寸、键盘和鼠标交互、防止背景滚动
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  headerExtra,
  children,
  footer,
  size = 'md',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className,
  contentClassName,
  bodyClassName,
  preventScroll = true,
  zIndex,
  ...props
}) => {
  const { t } = useTranslation()
  const modalRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  /**
   * 获取模态框容器样式
   */
  const getModalStyles = () => {
    return clsx(
      'fixed inset-0 flex items-center justify-center p-4',
      !zIndex && 'z-50',
      'animate-fade-in',
      className
    )
  }

  /**
   * 获取背景遮罩样式
   */
  const getBackdropStyles = () => {
    return clsx(
      'absolute inset-0 bg-black/50 backdrop-blur-sm',
      'transition-opacity duration-300'
    )
  }

  /**
   * 获取模态框内容区域样式
   */
  const getContentStyles = () => {
    return clsx(
      'relative bg-white dark:bg-gray-800 rounded-xl shadow-strong',
      'max-h-full overflow-hidden',
      'animate-slide-up',
      (footer || size === 'full') ? 'flex flex-col' : '',

      // 尺寸样式
      {
        'w-full max-w-sm': size === 'sm',
        'w-full max-w-md': size === 'md',
        'w-full max-w-2xl': size === 'lg',
        'w-full max-w-4xl': size === 'xl',
        'w-full h-full max-w-none': size === 'full',
      },

      contentClassName
    )
  }

  /**
   * 获取头部样式
   */
  const getHeaderStyles = () => {
    return clsx(
      'flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700',
      'bg-gray-50 dark:bg-gray-900/50'
    )
  }

  /**
   * 获取标题样式
   */
  const getTitleStyles = () => {
    return clsx(
      'text-lg font-semibold text-gray-900 dark:text-white',
      'truncate'
    )
  }

  /**
   * 获取主体内容样式
   */
  const getBodyStyles = () => {
    return clsx(
      'px-6 py-4',
      footer ? 'flex-1 overflow-y-auto' : 'overflow-y-auto max-h-[calc(100vh-8rem)]',
      bodyClassName
    )
  }

  /**
   * 获取关闭按钮样式
   */
  const getCloseButtonStyles = () => {
    return clsx(
      'p-1 rounded-md text-gray-400 hover:text-gray-600',
      'hover:bg-gray-100 dark:hover:bg-gray-700',
      'transition-colors duration-200'
    )
  }

  /**
   * 获取底部样式
   */
  const getFooterStyles = () => {
    return clsx(
      'flex-shrink-0 px-6 py-4 border-t border-gray-200 dark:border-gray-700',
      'bg-gray-50 dark:bg-gray-900/50'
    )
  }

  /**
   * 处理背景点击
   */
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && closeOnBackdropClick) {
      onClose()
    }
  }

  /**
   * 处理 ESC 键按下
   */
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && closeOnEscape) {
      onClose()
    }
  }

  /**
   * 保存和恢复焦点
   */
  const saveFocus = () => {
    previousFocusRef.current = document.activeElement as HTMLElement
  }

  const restoreFocus = () => {
    if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
      previousFocusRef.current.focus()
    }
  }

  /**
   * 禁止/恢复背景滚动
   */
  const preventBodyScroll = () => {
    if (preventScroll) {
      document.body.style.overflow = 'hidden'
    }
  }

  const allowBodyScroll = () => {
    if (preventScroll) {
      document.body.style.overflow = ''
    }
  }

  /**
   * 处理模态框打开
   */
  useEffect(() => {
    if (isOpen) {
      saveFocus()
      preventBodyScroll()
      
      // 添加键盘事件监听
      document.addEventListener('keydown', handleKeyDown)
      
      // 焦点移到模态框
      if (modalRef.current) {
        modalRef.current.focus()
      }
    } else {
      allowBodyScroll()
      restoreFocus()
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      allowBodyScroll()
      restoreFocus()
    }
  }, [isOpen, closeOnEscape, preventScroll])

  /**
   * 处理组件卸载
   */
  useEffect(() => {
    return () => {
      allowBodyScroll()
    }
  }, [])

  if (!isOpen) return null

  return (
    <div className={getModalStyles()} onClick={handleBackdropClick} style={zIndex ? { zIndex } : undefined} {...props}>
      <div className={getBackdropStyles()} />
      <div
        ref={modalRef}
        className={getContentStyles()}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {(title || showCloseButton) && (
          <div className={getHeaderStyles()}>
            {title && (
              <h2 id="modal-title" className={getTitleStyles()}>
                {title}
              </h2>
            )}
            <div className="flex items-center gap-2">
              {headerExtra}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className={getCloseButtonStyles()}
                  aria-label={t('modal.close')}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
        <div className={getBodyStyles()}>
          {children}
        </div>
        {footer && (
          <div className={getFooterStyles()}>
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Modal 组件的显示名称
 */
Modal.displayName = 'Modal'

/**
 * 导出 ModalProps 类型
 */
export type { ModalProps }